import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, RotateCw } from "lucide-react";
import { toast } from "sonner";

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";
const URL_ = "/hr/app/_erp/common-password";   // under /hr/app/ so the admin's HR session goes along

/* Users & Roles → HR common password (backend/hr_common_password.py). An
   admin gives every employee's HR app login one password — which is also
   how people imported from HR sign in to the ERP (employee code + HR
   password). Uses the admin's own HR sign-in; HR admin logins are never
   changed. Runs in the background on the server and reports progress. */
export default function HrCommonPassword() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [createMissing, setCreateMissing] = useState(true);
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    setError(""); setInfo(null);
    try { const { data } = await api.get(URL_); setInfo(data); } catch (e) { setError(errText(e)); }
  };
  const start = () => { setOpen(true); setPw(""); setPw2(""); setJob(null); load(); };
  useEffect(() => () => clearInterval(timer.current), []);

  const poll = (id) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`${URL_}/${id}`);
        setJob({ id, ...data });
        if (data.status !== "running") {
          clearInterval(timer.current);
          if (data.status === "finished") toast.success(`HR password set for ${data.done} employee login(s)`);
        }
      } catch (e) { clearInterval(timer.current); toast.error(errText(e)); }
    }, 1500);
  };

  const run = async () => {
    if (pw.length < (info?.min_length || 10) || !/[A-Za-z]/.test(pw) || !/\d/.test(pw)) {
      return toast.error(`Use at least ${info?.min_length || 10} characters, with letters and numbers (the HR app's rule)`);
    }
    if (pw !== pw2) return toast.error("The two passwords don't match");
    const n = (info?.logins || 0) + (createMissing ? info?.without_login || 0 : 0);
    if (!window.confirm(`Set this password for ${n} employee HR login(s)? Their current HR passwords stop working. HR admin logins are not changed.`)) return;
    try {
      const { data } = await api.post(URL_, { password: pw, create_missing: createMissing });
      setJob({ id: data.job_id, status: "running", done: 0, failed: [], total: data.total });
      poll(data.job_id);
    } catch (e) { toast.error(errText(e)); }
  };

  const running = job?.status === "running";
  return (
    <>
      <Button variant="outline" onClick={start} data-testid="hr-common-password-btn">
        <KeyRound className="w-4 h-4 mr-1.5" /> HR common password
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!running) setOpen(v); }}>
        <DialogContent className="max-w-lg" data-testid="hr-common-password">
          <DialogHeader>
            <DialogTitle>One HR password for all employees</DialogTitle>
            <DialogDescription>
              Sets the same password on every employee's HR app login. People imported from the HR app also use it to sign in
              to the ERP with their employee code. Admins only — HR admin logins, pending sign-up requests and deactivated
              logins are never changed.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 space-y-1">
              <div>{error}</div>
              <div className="flex gap-2 items-center">
                <Link to="/hr" className="text-blue-600 hover:underline" onClick={() => setOpen(false)}>Open HR → HR App</Link>
                <span className="text-slate-400">·</span>
                <button type="button" className="text-blue-600 hover:underline inline-flex items-center gap-1" onClick={load}><RotateCw className="w-3 h-3" />Try again</button>
              </div>
            </div>
          )}
          {!info && !error && <div className="text-sm text-slate-500">Reading the HR app's logins…</div>}

          {info && !job && (
            <div className="space-y-3">
              <div className="text-sm"><b>{info.logins}</b> employee login(s) in the HR app · <b>{info.without_login}</b> active employee(s) with no login yet</div>
              <div>
                <Label>New password</Label>
                <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="hr-common-pw" />
              </div>
              <div>
                <Label>Repeat it</Label>
                <PasswordInput value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" data-testid="hr-common-pw2" />
                <p className="text-[11px] text-slate-500 mt-1">At least {info.min_length} characters, with letters and numbers.</p>
              </div>
              {info.without_login > 0 && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={createMissing} onCheckedChange={(v) => setCreateMissing(!!v)} />
                  Also create HR logins for the {info.without_login} employee(s) who have none
                </label>
              )}
            </div>
          )}

          {job && (
            <div className="space-y-2" data-testid="hr-common-password-progress">
              <div className="text-sm">
                {running ? "Setting passwords…" : job.status === "finished" ? "Finished." : `Stopped: ${job.error || job.status}`}{" "}
                <b>{job.done}</b> of {job.total} done{job.failed?.length ? `, ${job.failed.length} failed` : ""}
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${job.total ? ((job.done + (job.failed?.length || 0)) / job.total) * 100 : 100}%` }} />
              </div>
              {job.failed?.length > 0 && (
                <ul className="max-h-40 overflow-y-auto text-xs text-red-700 space-y-0.5">
                  {job.failed.map((f) => <li key={f.username}><span className="font-mono">{f.username}</span> {f.name} — {f.error}</li>)}
                </ul>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={running}>Close</Button>
            {info && !job && (
              <Button onClick={run} className="bg-blue-600 hover:bg-blue-700" data-testid="hr-common-password-run">Set password</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
